import { RefObject, useMemo, useState } from 'react';

import { Song } from '~/interfaces';
import { Button } from '~/modules/elements/akui/button';
import getSongBeatLength from '~/modules/songs/utils/get-song-beat-length';
import isNotesSection from '~/modules/songs/utils/is-notes-section';
import { PlayerRef } from '~/routes/game/singing/player';

const SPEEDS = [0.5, 0.75, 1, 1.25];

/** Enough of the line to recognise the part, without the strip becoming a lyric sheet. */
const LABEL_LENGTH = 24;

interface Props {
  playerRef: RefObject<PlayerRef | null>;
  song: Song;
  onExit: () => void;
}

/**
 * Practice controls: slow the song down, and jump back to a part that needs work. Nothing sung
 * here is recorded - see the `practice` flag on `SingSetup`.
 */
export default function PracticeOverlay({ playerRef, song, onExit }: Props) {
  const [speed, setSpeed] = useState(1);

  const sections = useMemo(() => {
    const track = song.tracks[0];
    if (!track) return [];

    return track.sections.filter(isNotesSection).map((section, index) => ({
      index,
      start: section.start,
      label: section.notes[0]?.lyrics?.trim().slice(0, LABEL_LENGTH) || `Part ${index + 1}`,
    }));
  }, [song]);

  const changeSpeed = (next: number) => {
    setSpeed(next);
    playerRef.current?.setPlaybackSpeed(next);
  };

  const seekToSection = (start: number) => {
    // `start` is in beats from the song's beginning; the video is offset by the song's gap.
    playerRef.current?.seekTo((start * getSongBeatLength(song) + song.gap) / 1000);
  };

  return (
    <div
      // Fixed, not absolute: the game screen's containing block ends above the viewport, so an
      // absolutely positioned bar anchored to its bottom lands off-screen entirely.
      className="pointer-events-auto fixed inset-x-0 bottom-0 z-50 flex flex-col gap-2 bg-black/70 p-3 backdrop-blur-sm"
      data-test="practice-overlay">
      <div className="flex items-center gap-3">
        <span className="typography text-sm font-bold text-white/60">Practice · nothing is recorded</span>
        <div className="flex gap-1">
          {SPEEDS.map((option) => (
            <Button
              key={option}
              size="mini"
              className={option === speed ? 'border-active bg-active/30' : ''}
              onClick={() => changeSpeed(option)}
              data-test={`practice-speed-${option}`}
              data-active={option === speed}>
              {option}×
            </Button>
          ))}
        </div>
        <Button size="mini" className="ml-auto" onClick={onExit} data-test="practice-exit">
          Stop practising
        </Button>
      </div>

      {sections.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-1" data-test="practice-sections">
          {sections.map((section) => (
            <Button
              key={section.index}
              size="mini"
              className="ph-no-capture shrink-0"
              onClick={() => seekToSection(section.start)}
              data-test={`practice-section-${section.index}`}>
              {section.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
